import { useEffect, useMemo, useRef, useState } from "react";
import { Midi } from "@tonejs/midi";
import {
  playSequence, stopAllAudio, kickAudio, onAudioStop, SequenceEvent,
} from "@/audio/piano";
import { Keyboard } from "@/components/Keyboard";

// Midi.parse → Exercise. Each Tone.js MIDI note has time (s), duration (s),
// name (SPN like "C4"), velocity (0..1). We rebase time to beats using BPM.
function midiToExercise(buf: ArrayBuffer): Exercise | null {
  try {
    const midi = new Midi(buf);
    const bpm = midi.header.tempos[0]?.bpm ?? 120;
    const secPerBeat = 60 / bpm;
    const notes: NoteEvent[] = [];
    let maxBeat = 0;
    midi.tracks.forEach((track) => {
      track.notes.forEach((n) => {
        const time = n.time / secPerBeat;
        const duration = n.duration / secPerBeat;
        notes.push({
          pitch: n.name,
          time,
          duration,
          velocity: n.velocity,
        });
        maxBeat = Math.max(maxBeat, time + duration);
      });
    });
    notes.sort((a, b) => a.time - b.time);
    return {
      label: midi.name || "Imported MIDI file",
      bpm,
      totalBeats: Math.ceil(maxBeat),
      notes,
    };
  } catch (e) {
    console.error("MIDI parse failed", e);
    return null;
  }
}

/**
 * LessonPiano — an "intelligent" piano widget.
 *
 * Wraps the existing <Keyboard /> and adds a transport that can play back a
 * scripted exercise (or, later, a parsed MIDI file). Users can:
 *   • Press Play to run the lesson exercise back at variable tempo
 *   • Slow it down / speed it up
 *   • Loop it
 *   • Click any key directly (the original Keyboard behaviour still works)
 *   • Drop a .mid file onto the piano to play that instead of the lesson
 *
 * Playback rides the Tone.js transport, so note onsets and key highlights share
 * one clock and stay together however busy the page gets.
 *
 * The exercise is described as a list of {pitch, time, duration, …} events.
 * That data shape is deliberately compatible with what a MIDI parser would
 * emit, so we can plug MIDI files in later without changing the component
 * contract.
 */

export interface NoteEvent {
  /** SPN pitch — e.g. "C4", "F#5", "Bb2". */
  pitch: string;
  /** Onset time relative to start of exercise, in beats. */
  time: number;
  /** Duration in beats. */
  duration: number;
  /** Optional velocity 0..1. Default 0.85. */
  velocity?: number;
  /** Optional hand annotation — used for visual cues only. */
  hand?: "L" | "R";
}

export interface Exercise {
  /** Human-readable label shown next to the play button. */
  label: string;
  /** Beats per minute the exercise was scored at. The user can override. */
  bpm: number;
  /** All notes in the piece. Multiple notes can share a `time` for chords. */
  notes: NoteEvent[];
  /** Total beats of the exercise (used for loop boundary). */
  totalBeats: number;
}

interface LessonPianoProps {
  exercise: Exercise;
  /** Lowest visible C octave (default: smart-pick from exercise range). */
  startOctave?: number;
  /** Number of octaves visible (default: smart-pick). */
  octaves?: number;
  /** Static highlights to show when the piano is idle. */
  highlights?: { pitch: string; color: string; label?: string }[];
  /** Keyboard render height (px). */
  height?: number;
}

const SPEED_OPTIONS: { label: string; mul: number }[] = [
  { label: "0.5×", mul: 0.5 },
  { label: "0.75×", mul: 0.75 },
  { label: "1×", mul: 1 },
  { label: "1.25×", mul: 1.25 },
];

// Parse SPN pitch to MIDI number for octave-range calculation.
function pitchToMidi(p: string): number | null {
  const m = p.match(/^([A-G])(b|#)?(-?\d+)$/);
  if (!m) return null;
  const [, letter, acc, octStr] = m;
  const base: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
  let n = base[letter];
  if (acc === "#") n += 1;
  if (acc === "b") n -= 1;
  return (parseInt(octStr, 10) + 1) * 12 + n;
}

function inferOctaveRange(notes: NoteEvent[]): { startOctave: number; octaves: number } {
  if (notes.length === 0) return { startOctave: 3, octaves: 2 };
  const midis = notes.map((n) => pitchToMidi(n.pitch)).filter((m): m is number => m != null);
  if (midis.length === 0) return { startOctave: 3, octaves: 2 };
  const min = Math.min(...midis);
  const max = Math.max(...midis);
  const lowOct = Math.floor(min / 12) - 1;
  const highOct = Math.floor(max / 12) - 1;
  return {
    startOctave: Math.max(0, lowOct),
    octaves: Math.max(1, highOct - lowOct + 1),
  };
}

export function LessonPiano({
  exercise,
  startOctave: startOctaveProp,
  octaves: octavesProp,
  highlights = [],
  height = 180,
}: LessonPianoProps) {
  const [playing, setPlaying] = useState(false);
  const [loop, setLoop] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [activePitches, setActivePitches] = useState<Set<string>>(new Set());
  const [dragOver, setDragOver] = useState(false);
  const [overrideExercise, setOverrideExercise] = useState<Exercise | null>(null);
  /** Cleanup handle for the running sequence, from playSequence. */
  const stopRef = useRef<(() => void) | null>(null);

  // If the user has dropped a MIDI file, use that instead of the lesson exercise.
  const activeExercise = overrideExercise ?? exercise;

  const inferred = useMemo(() => inferOctaveRange(activeExercise.notes), [activeExercise.notes]);
  const startOctave = startOctaveProp ?? inferred.startOctave;
  const octaves = octavesProp ?? inferred.octaves;

  /** Stop the sound AND the UI. Releasing the sampler is the part that used to
   *  be missing — clearing timers left every triggered note ringing on. */
  const stopAll = () => {
    stopRef.current?.();
    stopRef.current = null;
    stopAllAudio();
    setActivePitches(new Set());
    setPlaying(false);
  };

  /** Another component took the transport — our audio is already gone. */
  useEffect(() => onAudioStop(() => {
    stopRef.current = null;
    setActivePitches(new Set());
    setPlaying(false);
  }), []);

  const playExercise = async () => {
    if (playing) {
      stopAll();
      return;
    }
    kickAudio();
    setPlaying(true);

    const secPerBeat = (60 / activeExercise.bpm) / speed;

    // Group notes by onset so a chord is one trigger.
    const byTime = new Map<number, NoteEvent[]>();
    for (const n of activeExercise.notes) {
      if (!byTime.has(n.time)) byTime.set(n.time, []);
      byTime.get(n.time)!.push(n);
    }
    const groups = [...byTime.entries()].sort((a, b) => a[0] - b[0]);

    // Everything rides the Tone.js audio clock. The old version scheduled note
    // onsets with window.setTimeout, which drifts under load and is clamped to
    // one second in a backgrounded tab — the exercise fell apart entirely.
    const events: SequenceEvent[] = groups.map(([time, notes]) => ({
      time: time * secPerBeat,
      notes: notes.map((e) => e.pitch),
      duration: Math.max(...notes.map((e) => e.duration)) * secPerBeat,
      velocity: notes[0].velocity ?? 0.85,
    }));

    stopRef.current = await playSequence(events, {
      bpm: activeExercise.bpm,
      loop,
      loopEnd: activeExercise.totalBeats * secPerBeat,
      // Highlights land on the same audio frame as the note, via Tone.Draw.
      onStep: (_i, ev) => setActivePitches(new Set(ev.notes)),
      onEnd: () => stopAll(),
    });
    // playSequence runs the global stop bus on its way in, which clears our own
    // playing flag through the listener above. Re-assert it now we're running.
    setPlaying(true);
  };

  useEffect(() => {
    return () => { stopRef.current?.(); stopRef.current = null; };
  }, []);

  // Build dynamic highlights — static highlights plus a glowing dot for any
  // currently-playing pitch.
  const liveHighlights = useMemo(() => {
    const out = [...highlights];
    activePitches.forEach((p) => {
      if (!out.find((h) => h.pitch === p)) {
        out.push({ pitch: p, color: "#f59e0b", label: "▶" });
      }
    });
    return out;
  }, [highlights, activePitches]);

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    if (!/\.midi?$/i.test(file.name)) {
      console.warn("Not a .mid/.midi file:", file.name);
      return;
    }
    const buf = await file.arrayBuffer();
    const ex = midiToExercise(buf);
    if (ex) setOverrideExercise(ex);
  };

  return (
    <div
      className={`my-4 rounded-2xl border bg-nsm-card/40 p-3 transition ${
        dragOver ? "border-amber-400 bg-amber-500/10" : "border-nsm-border"
      }`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      {/* Transport bar */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <button
          onClick={playExercise}
          className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-3 py-1.5 text-sm font-bold text-zinc-900 transition hover:bg-amber-400"
        >
          {playing ? "■ Stop" : "▶ Play"}
        </button>
        <span className="ml-1 text-[12px] text-nsm-dim">
          {activeExercise.label}
          {overrideExercise && (
            <button
              onClick={() => { stopAll(); setOverrideExercise(null); }}
              className="ml-2 text-[11px] text-amber-300 underline hover:text-amber-200"
            >
              ↺ reset to lesson
            </button>
          )}
        </span>

        <div className="ml-auto flex items-center gap-1 text-[11px] text-nsm-dim">
          <span className="mr-1 uppercase tracking-wider">Speed</span>
          {SPEED_OPTIONS.map((o) => (
            <button
              key={o.label}
              onClick={() => setSpeed(o.mul)}
              className={`rounded px-2 py-1 text-[11px] transition ${
                speed === o.mul
                  ? "bg-amber-500/20 text-amber-200"
                  : "text-nsm-dim hover:text-nsm-ink"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>

        <button
          onClick={() => setLoop((l) => !l)}
          className={`rounded px-2 py-1 text-[11px] uppercase tracking-wider transition ${
            loop ? "bg-emerald-500/20 text-emerald-200" : "text-nsm-dim hover:text-nsm-ink"
          }`}
        >
          ⟳ Loop
        </button>
      </div>

      <Keyboard
        startOctave={startOctave}
        octaves={octaves}
        highlights={liveHighlights}
        height={height}
      />

      <div className="mt-2 flex items-center justify-between text-[11px] text-nsm-dim">
        <span>
          ▶ plays the lesson exercise on the piano above. Click any key to play it
          manually. {loop ? "Looping until you press Stop." : "One-shot playback."}
        </span>
        <span className={dragOver ? "text-amber-300" : "text-nsm-dim"}>
          {dragOver ? "⇣ drop your .mid file" : "Drop a .mid file to play it here"}
        </span>
      </div>
    </div>
  );
}
